/**
 * The shared behaviour of every menu screen: a column of choices driven by both
 * keyboard and mouse, a drifting backdrop, and a redraw budget (20 Hz — the
 * marine snow is the only thing moving, and it drifts slowly on purpose).
 *
 * The column scrolls. It has to: at UI scale 2 a 720 px window leaves 360 UI px
 * for everything, which is less than a full list needs (doc 17 §1). Selection
 * always drags the view with it, the wheel works, and a scrollbar says so.
 */

import { audio } from '../../audio/engine.ts';
import { PixelSurface } from '../../render/surface.ts';
import {
  clampScroll,
  drawBackdrop,
  drawFrame,
  drawMenu,
  menuHit,
  menuMetrics,
  type FrameOpts,
  type MenuItem,
} from '../../ui/menu.ts';
import type { Rect } from '../../ui/widgets.ts';
import type { App, Screen } from '../app.ts';

export abstract class MenuScreen implements Screen {
  protected readonly app: App;
  protected selected = 0;
  protected hover = -1;
  protected rects: Rect[] = [];
  protected dirty = true;
  protected scroll = 0;
  /** The column's rect from the last frame — mouse hits are clipped to it. */
  private view: Rect | null = null;
  private lastDraw = 0;
  private readonly started = performance.now();

  constructor(app: App) {
    this.app = app;
  }

  protected abstract frame(): FrameOpts;
  protected abstract items(): MenuItem[];
  protected abstract activate(index: number): void;

  /** Draw anything beside the menu column — panels, portraits, tallies. */
  protected detail(_s: PixelSurface, _body: Rect): void {}
  /** ESC / right-click. Default: nothing, so a root screen cannot be escaped. */
  protected back(): void {}
  /**
   * Menu column width; the rest of the body belongs to `detail`. Capped so a
   * wide window gets a readable column instead of one stretched row per line.
   */
  protected columnWidth(body: Rect): number {
    return Math.min(body.w, 520);
  }
  /** Screens that need their own keys (toggles, paste) hook in here. */
  protected key(_k: string, _e: KeyboardEvent): void {}

  /** Scroll just enough to put row `index` fully inside the column. */
  private follow(index: number): void {
    if (!this.view) return;
    const m = menuMetrics(this.items());
    const top = m.offsets[index] ?? 0;
    const bottom = top + m.step;
    if (top < this.scroll) this.scroll = top;
    else if (bottom > this.scroll + this.view.h) this.scroll = bottom - this.view.h;
    this.scroll = clampScroll(this.scroll, this.view.h, m.contentH);
  }

  private move(delta: number): void {
    const items = this.items();
    if (items.length === 0) return;
    let next = this.selected;
    for (let k = 0; k < items.length; k++) {
      next = (next + delta + items.length) % items.length;
      if (!items[next]!.disabled) break;
    }
    if (next !== this.selected) audio.play('ui_move');
    this.selected = next;
    this.follow(next);
    this.dirty = true;
  }

  onKey(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (k === 'arrowdown' || k === 's') this.move(1);
    else if (k === 'arrowup' || k === 'w') this.move(-1);
    else if (k === 'enter' || k === ' ') {
      e.preventDefault();
      const it = this.items()[this.selected];
      audio.play(it && !it.disabled ? 'ui_select' : 'ui_deny');
      if (it && !it.disabled) this.activate(this.selected);
    } else if (k === 'escape') {
      audio.play('ui_back');
      this.back();
    }
    else {
      const items = this.items();
      const hotkeyed = items.findIndex((i) => i.hotkey?.toLowerCase() === k);
      if (hotkeyed >= 0 && !items[hotkeyed]!.disabled) {
        this.selected = hotkeyed;
        this.follow(hotkeyed);
        this.activate(hotkeyed);
      } else {
        this.key(k, e);
      }
    }
    this.dirty = true;
  }

  onWheel(e: WheelEvent): void {
    if (!this.view) return;
    e.preventDefault();
    const m = menuMetrics(this.items());
    this.scroll = clampScroll(this.scroll + Math.sign(e.deltaY) * m.step, this.view.h, m.contentH);
    this.dirty = true;
  }

  onMouseMove(e: MouseEvent): void {
    const { x, y } = this.app.ui(e);
    const h = menuHit(this.rects, x, y, this.view ?? undefined);
    if (h !== this.hover) {
      this.hover = h;
      this.dirty = true;
    }
  }

  onMouseDown(e: MouseEvent): void {
    if (e.button === 2) {
      audio.play('ui_back');
      this.back();
      return;
    }
    const { x, y } = this.app.ui(e);
    const h = menuHit(this.rects, x, y, this.view ?? undefined);
    const items = this.items();
    if (h >= 0) audio.play(items[h]!.disabled ? 'ui_deny' : 'ui_select');
    if (h >= 0 && !items[h]!.disabled) {
      this.selected = h;
      this.activate(h);
    }
    this.dirty = true;
  }

  resize(): void {
    this.view = null; // recomputed on the next draw, at the new size
    this.dirty = true;
  }

  update(now: number): void {
    if (!this.dirty && now - this.lastDraw < 50) return;
    const s = this.app.surface;
    // doc 18 §3: reduced motion stills the drift. The seabed is still drawn —
    // an empty screen is not an accessibility feature.
    drawBackdrop(s, this.app.reducedMotion ? 0 : (now - this.started) / 1000);
    const body = drawFrame(s, this.frame());
    const items = this.items();
    const view: Rect = { ...body, w: this.columnWidth(body) };
    const first = this.view === null;
    this.view = view;
    if (first) this.follow(this.selected);
    this.scroll = clampScroll(this.scroll, view.h, menuMetrics(items).contentH);
    this.rects = drawMenu(s, view, items, this.selected, this.hover, this.scroll);
    this.detail(s, body);
    this.app.hud.upload();
    this.lastDraw = now;
    this.dirty = false;
  }

  destroy(): void {}
}
